import React from 'react';
import { submitState } from '../lineState';
import Dialog from '../../../components/Dialog';
import { IconDoc } from '../../../components/dialogIcons';

/*
  Handing a count sheet in.

  Built to Group E's two approved drawings on 5 September 2026,
  `dialog-submit-*.png` and `dialog-submit-ready-*.png`.

  **It let a rep submit a sheet with lines nobody had counted, and told them
  nothing useful when the database refused.** It showed items, units and flagged,
  every one of them off `item.quantity || 0`, so a line never counted looked
  exactly like a line counted as none. On Michelle Chelette's Main Regional
  Office sheet it read 300 items and offered "Yes, Submit Count" with **75 lines
  never counted.**

  **Submit is locked until every line has a number**, and the dialog says how
  many are still blank and what a blank means. That is the whole point of the
  two drawings: the one with work left is a gold band and a grey button, and the
  one that is ready is a green band and a red one.

  **THE READY BUTTON IS BLUE NOW, NOT RED, AND THAT DEPARTS FROM THE APPROVED
  DRAWING.** Changed 12 September 2026 on the owner's own instruction, after a
  design review named this the emotional low point of the product.

  The reason, in his terms. A rep drives to a location and counts eighty-nine
  lines. The moment they finish, the button they press was Critical red, under a
  padlock, and the head of the dialog carried a red square as well. The brand
  guide defines Critical as errors, stockouts, destructive acts and an overdue
  count. **Handing in finished work is none of the four.** Three separate things
  were saying hazard about the one act in OnHand a rep is most proud of.

  So: the head is `info` with a document mark rather than `danger` with a
  padlock, and the act is `btn-primary`. **The gate is untouched.** Submit is
  still locked until every line has a number, the grey disabled button still
  says so honestly rather than hiding, and the band still turns gold or green.
  Nothing about what the dialog refuses has changed, only what it accuses the
  rep of.

  **The drawings `dialog-submit-*.png` still show red.** They are the record for
  this screen and they now disagree with it in one respect. This paragraph is
  the departure, written beside the thing that departed, which is what this
  project does instead of quietly redrawing.

  **A zero is not a blank and the third figure says which it is showing.** With
  work left it counts what is still blank; with none left it counts what was
  counted honestly as none, which is a number an administrator reviewing the
  sheet actually wants.
*/

function Figure({ n, label, dot }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 6, whiteSpace: 'nowrap' }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: dot, alignSelf: 'center', flexShrink: 0 }} />
      <b style={{ fontSize: 14, fontWeight: 800, color: 'var(--text-strong)' }}>{typeof n === 'number' ? n.toLocaleString() : n}</b>
      <span style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>{label}</span>
    </span>
  );
}

export default function SubmitModal({ count, items = [], totalUnits, onConfirm, onClose }) {
  // The counting lives in `submitState`, beside the screen it guards, so the
  // rule the database enforces and the rule this dialog draws are one thing.
  const { total, blank, zeroes, counted, ready } = submitState(items);

  return (
    <Dialog
      kind="info" icon={<IconDoc />}
      title="Submit this count"
      record={count?.warehouse?.name}
      says="For review and approval."
      onClose={onClose}
      footer={
        <>
          <button className="btn btn-quiet" onClick={onClose}>Go back</button>
          {/* Rule 33 and rule 34: the act is out, and it is honestly
              unavailable rather than hidden, so a rep can see what it is
              waiting for. */}
          <button className="btn btn-primary" onClick={onConfirm} disabled={!ready}>Submit the count</button>
        </>
      }
    >
      {/* Rule 5: the live numbers sit on hairlines, not in tiles.

          **Two by two on a phone, with no hairlines at all.** Four figures and
          three rules do not fit across 390, and letting them wrap left a
          hairline hanging off the end of the first row with nothing after it. */}
      <div className="figure-strip" style={{ paddingBottom: 14, borderBottom: '1px solid var(--border-soft)' }}>
        <Figure n={total} label="Lines" dot="var(--text-primary)" />
        <span className="tb-vr" style={{ margin: '0 14px' }} />
        <Figure n={counted} label="Counted" dot="var(--success)" />
        <span className="tb-vr" style={{ margin: '0 14px' }} />
        {ready
          ? <Figure n={zeroes} label="Counted as zero" dot="var(--critical)" />
          : <Figure n={blank}  label="Still blank"     dot="var(--text-muted)" />}
        <span className="tb-vr" style={{ margin: '0 14px' }} />
        <Figure n={totalUnits} label="Units" dot="var(--text-primary)" />
      </div>

      <div style={{
        marginTop: 16, borderRadius: 12, padding: '13px 15px', fontSize: 13, lineHeight: 1.55,
        background: ready ? 'var(--success-bg)' : 'var(--warning-bg)',
        color: ready ? 'var(--success-ink)' : 'var(--warning-ink)',
      }}>
        {ready
          ? 'Every line has a number. Once submitted the sheet locks, and changing it means asking an admin to reopen it.'
          : blank + (blank === 1 ? ' line is' : ' lines are') + ' still blank. Submit unlocks when every line has a number. A blank is not a zero: it means nobody has been to that shelf.'}
      </div>
    </Dialog>
  );
}
